import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { obtenerCuenta, renovarTarjeta } from '../api/cuentasApi.js';
import { importe, vigencia } from '../formato.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

const CLAVE_EFECTIVO = 'EFECTIVO';

const MOTIVOS = [
  { valor: 'VENCIMIENTO', titulo: 'Se venció', ayuda: 'El plástico llegó a su fecha de vigencia y el banco mandó uno nuevo.' },
  { valor: 'EXTRAVIO', titulo: 'Se extravió o me la robaron', ayuda: 'El banco repuso el plástico con otros dígitos.' },
  { valor: 'OTRO', titulo: 'Otro motivo', ayuda: 'Cambio de producto, reposición por daño, cambio de banco emisor del mismo producto.' },
];

/**
 * Renovación del plástico (RF-046, D-26) en tres pasos: motivo, datos nuevos y
 * confirmación con el antes/después.
 *
 * ES LA MISMA CUENTA. No crea una cuenta nueva, no da de baja la anterior y no
 * copia ningún saldo: conserva identificador, propietario, saldo o deuda e
 * historial completo, porque no se cambia de fila (D-18, D-24, RN-070a).
 *
 * EXISTE APARTE DE LA EDICIÓN por una razón concreta y no cosmética: necesita
 * capturar el MOTIVO para dejarlo en la bitácora (RN-070d).
 *
 * NO existe en EFECTIVO (D-28): ahí ni siquiera se llega a esta pantalla, y si la
 * petición llegara directa a la API el servidor responde RENOVACION_NO_APLICA.
 */
export default function RenovarTarjeta() {
  const { idCuenta } = useParams();
  const navegar = useNavigate();

  const [cuenta, setCuenta] = useState(null);
  const [paso, setPaso] = useState(1);
  const [motivo, setMotivo] = useState('VENCIMIENTO');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [formulario, setFormulario] = useState({
    cuatroUltimosDigitos: '', vigenciaMes: '', vigenciaAnio: '',
    nombre: '', limiteCredito: '', diaCorte: '', diaLimitePago: '',
  });

  useEffect(() => {
    obtenerCuenta(idCuenta)
      .then((datos) => {
        setCuenta(datos);
        setFormulario((previo) => ({
          ...previo,
          nombre: datos.nombre,
          limiteCredito: datos.limiteCredito ?? '',
          diaCorte: datos.diaCorte ?? '',
          diaLimitePago: datos.diaLimitePago ?? '',
        }));
      })
      .catch(setError);
  }, [idCuenta]);

  const cambiar = (campo) => (evento) => setFormulario((previo) => ({ ...previo, [campo]: evento.target.value }));

  async function confirmar() {
    setError(null);
    setGuardando(true);
    try {
      // Lo que NO viaja jamás, ni aquí ni en ningún otro lado de esta pantalla:
      // propietario, moneda, tipo y saldo o deuda inicial. Renovar no es una vía
      // alterna para cambiar lo que D-20 y D-23 congelaron (RN-070c).
      await guardarConConexion(() => renovarTarjeta(idCuenta, {
        motivo,
        cuatroUltimosDigitos: formulario.cuatroUltimosDigitos || null,
        vigenciaMes: formulario.vigenciaMes ? Number(formulario.vigenciaMes) : null,
        vigenciaAnio: formulario.vigenciaAnio ? Number(formulario.vigenciaAnio) : null,
        nombre: formulario.nombre || null,
        limiteCredito: cuenta?.esCredito ? String(formulario.limiteCredito) : null,
        diaCorte: cuenta?.esCredito && formulario.diaCorte ? Number(formulario.diaCorte) : null,
        diaLimitePago: cuenta?.esCredito && formulario.diaLimitePago ? Number(formulario.diaLimitePago) : null,
        version: cuenta?.version,
      }));

      // RF-148, RN-391: REEMPLAZA el paso del formulario en vez de dejarlo debajo.
      // Antes empujaba, y el botón atrás devolvía al paso ya cumplido.
      navegar(`/cuentas/${idCuenta}`, { replace: true });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  if (!cuenta) {
    return (
      <Shell titulo="Renovar plástico" volverA={`/cuentas/${idCuenta}`}>
        {error ? <AvisoDeError error={error} /> : <div className="aviso i">Cargando…</div>}
      </Shell>
    );
  }

  if (cuenta.claveTipoCuenta === CLAVE_EFECTIVO) {
    return (
      <Shell titulo="Renovar plástico" volverA={`/cuentas/${idCuenta}`}>
        {/* Fila 5 del inventario del incremento 15 (RF-222): salió la justificación del
            diseño («no existe para este tipo de cuenta»); queda el texto literal de §4.
            Rev. 4 (D-331): se adopta la segunda frase del prototipo v33, que dice qué
            hacer. Es cambio de TEXTO, no de navegación: no se agrega enlace ni botón; el
            camino sigue siendo la flecha de volver del encabezado, que ya apunta al
            detalle de la cuenta (volverA, arriba). */}
        <div className="aviso e" data-prueba="renovar-no-disponible">
          ✖ Esta operación no está disponible para una cuenta de efectivo. Vuelve al
          detalle de la cuenta para ver lo que sí puedes hacer con ella.
        </div>
      </Shell>
    );
  }

  return (
    <Shell titulo="Renové mi plástico" subtitulo={`Paso ${paso} de 3 · ${cuenta.nombre}`} volverA={`/cuentas/${idCuenta}`}>
      <AvisoDeError error={error} />

      {paso === 1 && (
        <>
          <div className="aviso i">
            Esta operación existe <b>aparte de la edición</b> por una razón concreta: necesita
            capturar el <b>motivo</b> para dejarlo en la bitácora, y así “¿cuándo y por qué se renovó
            esta tarjeta?” tiene respuesta sin agregar un solo campo a la cuenta.
          </div>

          <div className="card lista">
            {MOTIVOS.map((opcion) => (
              <button
                key={opcion.valor}
                type="button"
                className="fila"
                style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, borderBottom: '1px solid var(--lin)' }}
                aria-pressed={motivo === opcion.valor}
                onClick={() => setMotivo(opcion.valor)}
              >
                <div className="crece">
                  <b>{motivo === opcion.valor ? '● ' : '○ '}{opcion.titulo}</b>
                  <small>{opcion.ayuda}</small>
                </div>
              </button>
            ))}
          </div>

          {motivo === 'EXTRAVIO' && (
            <div className="aviso w">
              <b>Si aún no llega el plástico nuevo, no desactives la cuenta.</b> La deuda sigue
              existiendo y la baja estaría bloqueada igual. Se deja como está y se renueva
              cuando llegue.
            </div>
          )}

          <div style={{ display: 'grid', gap: 8, margin: 12 }}>
            <button className="btn" type="button" onClick={() => setPaso(2)}>Continuar</button>
            <button className="btn sec" type="button" onClick={() => navegar(`/cuentas/${idCuenta}`, { replace: true })}>Cancelar</button>
          </div>
        </>
      )}

      {paso === 2 && (
        <>
          <div className="card">
            <h3>Datos del plástico nuevo</h3>

            <label htmlFor="cuatroUltimosDigitos">Cuatro últimos dígitos · opcional</label>
            <input id="cuatroUltimosDigitos" inputMode="numeric" maxLength={4} placeholder="7213"
              value={formulario.cuatroUltimosDigitos} onChange={cambiar('cuatroUltimosDigitos')} />
            <span className="ayuda">
              Los del plástico nuevo. Cambian en cada renovación y <b>ese cambio no altera nada más</b>.
            </span>

            <label htmlFor="vigenciaMes">Vigencia nueva · opcional · las dos o ninguna</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="vigenciaMes" inputMode="numeric" maxLength={2} placeholder="Mes (9)"
                value={formulario.vigenciaMes} onChange={cambiar('vigenciaMes')} />
              <input aria-label="Año de vigencia" inputMode="numeric" maxLength={4} placeholder="Año (2031)"
                value={formulario.vigenciaAnio} onChange={cambiar('vigenciaAnio')} />
            </div>

            <label htmlFor="nombre">Nombre · por si de paso lo renombras</label>
            <input id="nombre" maxLength={100} value={formulario.nombre} onChange={cambiar('nombre')} />
            <span className="ayuda">Si lo cambias, la cuenta se mueve de lugar en la lista, que es alfabética.</span>
          </div>

          {cuenta.esCredito && (
            <div className="card">
              <h3>Solo si el banco los cambió</h3>

              <label htmlFor="limiteCredito">Límite de crédito</label>
              {/* Regla única de campos de importe (RF-086), en la divisa de la
                  tarjeta: no hay conversión (RN-138). */}
              <CampoDeImporte
                id="limiteCredito"
                valor={formulario.limiteCredito}
                alCambiar={(valorNuevo) => setFormulario((previo) => ({ ...previo, limiteCredito: valorNuevo }))}
                codigoDivisa={cuenta.codigoISO}
                contexto="esta tarjeta"
              />

              <label htmlFor="diaCorte">Día de corte y día límite de pago</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input id="diaCorte" inputMode="numeric" maxLength={2} value={formulario.diaCorte} onChange={cambiar('diaCorte')} />
                <input aria-label="Día límite de pago" inputMode="numeric" maxLength={2}
                  value={formulario.diaLimitePago} onChange={cambiar('diaLimitePago')} />
              </div>

              <div className="aviso w">
                Si cambias el corte o el día de pago, el cambio <b>rige de hoy en adelante</b>: las
                fechas ya calculadas del pasado <b>no se recalculan</b> y el histórico de vencimientos
                no se reescribe.
              </div>
            </div>
          )}

          <div className="card">
            <h3>Lo que no se toca ni renovando</h3>
            <div className="fila"><span className="crece">Moneda</span><b>{cuenta.codigoISO}</b></div>
            <div className="fila"><span className="crece">Tipo de cuenta</span><b>{cuenta.nombreTipoCuenta}</b></div>
            <div className="fila">
              <span className="crece">{cuenta.esCredito ? 'Deuda inicial' : 'Saldo inicial'}</span>
              <b>{importe(cuenta.saldoInicial, cuenta.codigoISO)}</b>
            </div>
            {/* Fila 6 del inventario del incremento 15 (RF-222/RF-223): se conserva la
                primera frase y sale la segunda, que citaba la petición y el código de
                catálogo CAMPO_NO_ADMITIDO. */}
            <div className="ayuda" data-prueba="renovar-datos-fijos">
              <b>Renovar no es una vía alterna para cambiar lo que quedó fijo al crear la cuenta.</b>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8, margin: 12 }}>
            <button className="btn" type="button" onClick={() => setPaso(3)}>Continuar</button>
            <button className="btn sec" type="button" onClick={() => setPaso(1)}>Atrás</button>
          </div>
        </>
      )}

      {paso === 3 && (
        <>
          {/* Fila 7 del inventario del incremento 15 (RF-223, D-332, rev. 4): además de la
              jerga de base de datos («porque no se cambia de fila»), cae la mención del
              identificador interno. Tras retirar el renglón «#N → #N» (fila 8, D-327 /
              RN-628), hablarle a la persona de un dato que ya no ve es hablarle de la base
              de datos; el patrón RN-627(d) lo afirma ahora por palabra. El aviso dibujado
              en el prototipo v33 es escenografía (idéntico en las dos columnas y además
              inexacto en crédito): rige el literal de §4 (RN-629). */}
          <div className="aviso ok" data-prueba="renovar-misma-cuenta">
            <b>Es la misma cuenta.</b> Lo único que cambia son los datos del plástico. El
            propietario, {cuenta.esCredito ? 'la deuda' : 'el saldo'} y los
            movimientos <b>siguen siendo los mismos</b>.
          </div>

          {/* Fila 8 del inventario del incremento 15 (RF-223, D-325): salió el renglón
              «Identificador de la cuenta · #N → #N». El aviso de arriba ya dice que es la
              misma cuenta y los renglones que quedan lo demuestran con datos que sí le
              dicen algo a la persona. */}
          <div className="card lista" data-prueba="renovar-antes-despues">
            <h3>Antes → después</h3>
            <div className="fila"><span className="crece">Propietario</span><b>{cuenta.nombrePropietario}</b></div>
            <div className="fila">
              <span className="crece">{cuenta.esCredito ? 'Deuda actual' : 'Saldo actual'}</span>
              <b>{importe(cuenta.esCredito ? cuenta.deuda : cuenta.saldo, cuenta.codigoISO)} → sin cambio</b>
            </div>
            <div className="fila"><span className="crece">Movimientos</span><b>{cuenta.cantidadMovimientos} → sin cambio</b></div>
            <div className="fila">
              <span className="crece">Cuatro últimos dígitos</span>
              <b>•• {cuenta.cuatroUltimosDigitos ?? '—'} → •• {formulario.cuatroUltimosDigitos || (cuenta.cuatroUltimosDigitos ?? '—')}</b>
            </div>
            <div className="fila">
              <span className="crece">Vigencia</span>
              <b>
                {vigencia(cuenta.vigenciaMes, cuenta.vigenciaAnio) ?? '—'} →
                {' '}{vigencia(Number(formulario.vigenciaMes), Number(formulario.vigenciaAnio)) ?? (vigencia(cuenta.vigenciaMes, cuenta.vigenciaAnio) ?? '—')}
              </b>
            </div>
            <div className="fila"><span className="crece">Motivo</span><b>{MOTIVOS.find((m) => m.valor === motivo)?.titulo}</b></div>
          </div>

          {/* Fila 9 del inventario del incremento 15 (RF-222/RF-223): salió el código
              técnico RENOVACION_{'{motivo}'} y la justificación de la implementación. La
              palabra «bitácora» se conserva como concepto de rastro (RN-630e). */}
          <div className="aviso i" data-prueba="renovar-bitacora">
            Queda un asiento en la <b>bitácora</b> con quién renovó, cuándo, el valor anterior y el
            nuevo de cada campo, y el motivo que elegiste.
          </div>

          <div style={{ display: 'grid', gap: 8, margin: 12 }}>
            <button className="btn" type="button" onClick={confirmar} disabled={guardando}>
              {guardando ? 'Guardando…' : 'Confirmar renovación'}
            </button>
            <button className="btn sec" type="button" onClick={() => setPaso(2)}>Atrás</button>
          </div>
        </>
      )}
    </Shell>
  );
}
